/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiText, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyTopologyElement, SliderInstanceType } from '@modules/ui-types'

import { getSliderNodeTypeForTopologyElement } from '@/lib/stackDeployments/selectors/nodeRoles'

import prettySize from '../../../../../../lib/prettySize'

import { getKeys, getNumber, getCpuDisplayText } from './helpers'

import type { NormalizeSizingProps } from './NormalizeSizing/NormalizeSizing'

type Props = Pick<
  NormalizeSizingProps,
  'size' | 'resource' | 'storageMultiplier' | 'cpuMultiplier' | 'maxSize' | 'instanceConfiguration'
> & {
  zoneCount: number
  autoscalingMin: number | undefined
  isMachineLearning: boolean
  isFrozen: boolean
  sliderInstanceType?: SliderInstanceType
  topologyElement: AnyTopologyElement
  textSize?: `xs` | `s` | `m`
  tiebreaker?: boolean
}

const keyLabels = {
  memory: `RAM`,
  storage: `storage`,
  cpu: `vCPU`,
}

const Summary: React.FunctionComponent<Props> = ({
  size,
  resource: instanceResource,
  storageMultiplier,
  cpuMultiplier,
  zoneCount,
  maxSize,
  isMachineLearning,
  isFrozen,
  autoscalingMin,
  sliderInstanceType,
  topologyElement,
  textSize = 's',
  tiebreaker,
}) => {
  const sliderNodeType = tiebreaker
    ? `tiebreaker`
    : getSliderNodeTypeForTopologyElement({ topologyElement })
  const { primaryKey, secondaryKey } = getKeys({
    sliderNodeType,
    sliderInstanceType,
    instanceResource,
    storageMultiplier,
  })

  const displaySize = getDisplaySize({
    autoscalingMin,
    maxSize,
    size,
    isMachineLearning,
  })
  const primaryText = `${prettySize(
    getNumber({
      resourceIn: instanceResource,
      storageMultiplier,
      totalSize: displaySize * zoneCount,
      resourceOut: primaryKey,
      isBlobStorage: isFrozen,
    }),
  )} ${keyLabels[primaryKey || `memory`]}`
  const secondaryText = secondaryKey
    ? `${prettySize(
        getNumber({
          resourceIn: instanceResource,
          storageMultiplier,
          totalSize: displaySize * zoneCount,
          resourceOut: secondaryKey,
          isBlobStorage: isFrozen,
        }),
      )} ${keyLabels[secondaryKey]}`
    : ``

  const cpuText = getCpuDisplayText({
    totalSize: displaySize,
    zoneCount,
    isMachineLearning,
    cpuMultiplier,
  })

  return (
    <div data-test-subj='topologySummary'>
      <EuiFlexGroup gutterSize='s' responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiText size={textSize}>
            {primaryText}
            {isFrozen && (
              <React.Fragment>
                {/* Leading space is intentional to have space between primaryText and cached storage text */}{' '}
                <FormattedMessage
                  id='summary.cached-storage-text'
                  defaultMessage='({cachedStorage} cached storage)'
                  values={{
                    cachedStorage: prettySize(
                      getNumber({
                        resourceIn: instanceResource,
                        storageMultiplier,
                        totalSize: size * zoneCount,
                        resourceOut: primaryKey,
                        isBlobStorage: false,
                      }),
                    ),
                  }}
                />
              </React.Fragment>
            )}
          </EuiText>
        </EuiFlexItem>
        {secondaryKey && (
          <Fragment>
            <EuiFlexItem grow={false}>
              <EuiText size={textSize} color='subdued'>
                &#124;
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size={textSize}>{secondaryText}</EuiText>
            </EuiFlexItem>
          </Fragment>
        )}
        {cpuMultiplier && (
          <Fragment>
            <EuiFlexItem grow={false}>
              <EuiText size={textSize} color='subdued'>
                &#124;
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size={textSize}>{cpuText}</EuiText>
            </EuiFlexItem>
          </Fragment>
        )}
      </EuiFlexGroup>
    </div>
  )
}

function getDisplaySize({ autoscalingMin, maxSize, size, isMachineLearning }) {
  const showMachineLearningAutoscaleMinForSize =
    isMachineLearning && autoscalingMin! > 0 && (size === 0 || !isFinite(size))

  // Show size as actual size from the API if:
  // a) this isn't machine learning
  // OR
  // b) it is machine learning, and has a non-zero size already, and the user has selected an autoscalingMin greater than 0
  if (!showMachineLearningAutoscaleMinForSize) {
    return size
  }

  // Show size as autoscalingMin in the case that the user has just set
  // the min to greater than 0, so the API still has size: 0
  // We do this because we never send size in the request for ML, so don't
  // want to update it or track it, but do want to communicate to the user
  // that they will effectively be turning ML on
  if (autoscalingMin! > maxSize) {
    const displaySize = maxSize

    return displaySize
  }

  return autoscalingMin
}

export default Summary
